import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import useInterval from '../../../../hooks/useInterval';
import { mod } from '../../../../utils';
import DisplayComponent from '../../BaseComponents/DisplayComponent';

import './GenericCarousel.module.css';

const parsePercentage = value => {
  if (!value || !value.length) {
    return 0;
  }
  if (value[value.length - 1] !== '%') {
    return 0;
  }
  return parseInt(value.substring(0, value.length - 1));
};

// creates a range from 0 to `length - 1`
// with elements shifted `shiftedBy` times
// shiftedRange(5, 2) => [3, 4, 0, 1, 2]
// shiftedRange(6, 3) => [3, 4, 5, 0, 1, 2]
const shiftedRange = (length, shiftedBy) => {
  const range = [...Array(length).keys()];
  return range
    .slice(length - shiftedBy)
    .concat(range.slice(0, length - shiftedBy));
};

// creates an array of numbers from 0 to `length - 1`
// where `index` is in the center
const rangeWithCenteredIndex = (index, length) => {
  if (length === 1) {
    return [0, 0, 0];
  }
  if (length === 2) {
    return index === 0 ? [1, 0, 1] : [0, 1, 0];
  }
  return shiftedRange(length, mod(Math.floor(length / 2 - index), length));
};

/******************************************************************
 * understanding the basic idea behind this carousel
 *
 *     modes:
 *
 *        - this carousel can be in "focus mode" or "slide mode"
 *        - "focus mode" is initiated when the user tabs/focuses to the carousel
 *        - the carousel is always in "slide mode" otherwise
 *
 *        - in "focus mode", the carousel is rendered as a strip of items in chronological order,
 *           starting with the first item
 *        - on entering "focus mode", the user always sees the first item in the viewable area, even if
 *           another item was shown in the viewable area before entering "focus mode"
 *        - the user can now tab through the strip of items, which shifts each successive item into the viewable area
 *        - upon tabbing through the last item in the strip, focus is switched to the next focusable thing on the page
 *           after the carousel
 *        - note that the carousel is NOT infinite in focus mode, to avoid trapping the user
 *        - upon tabbing out of the carousel, the mode switches back to "slide mode"
 *
 *        - in "slide mode", the carousel is rendered as a strip of items in chronological order, but the
 *           currently visible slide is always centered in this strip
 *           if there are 1 or 2 slides, some slides are duplicated so that the center slide always has some slides
 *           to the left and right
 *        - this allows the carousel to slide in either direction
 *        - when a slide interaction starts, the strip is animated to the left or right, to reveal the
 *           desired new slide
 *        - after the animation ends, the strip is seamlessly re-rendered with the new slide in the center,
 *           still in chronological order, so the carousel can continue to slide in either direction,
 *           giving the illusion of an "infinite carousel"
 *        - in "slide mode", the user can also click on the carousel to drag it in either direction
 *           if draggable === `true`
 *        - the carousel moves as the user drags it, then completes a slide interaction when the user releases the drag
 *
 *     timer:
 *
 *        - the carousel can be autoplayed by setting the `autoplaying` prop, but autoplay
 *           always pauses in the following situations:
 *            + the carousel is in "focus mode"
 *            + the mouse is hovering over the carousel
 *            + the carousel is being dragged
 *           autoplay will resume after existing one of these cases
 *           if the carousel was autoplaying before it entered one of the above cases
 *
 *     control:
 *
 *        - this carousel is a controlled component, meaning it can be controlled entirely through props
 *        - pass a new item number as `nextSlide` to prompt the carousel to slide to that item
 *           NOTE: don't trigger a slide when the carousel is already sliding
 *                 (it won't break, but it looks kind of ugly)
 *        - pass a boolean to `autoplaying` to prompt the carousel to pause or play the timer
 *           NOTE: even if autoplaying === true, the timer still won't play in the above listed cases
 *        - pass 1 or -1 as `nextDirection` to set the preferred direction to slide to the next slide
 *          where 1 is right and -1 is left
 *           NOTE: this only has an effect in the case where there is only 1 or 2 items in the carousel
 *                 (because this is the only case where it's valid to slide in either direction)
 *                 all other cases ignore nextDirection, because there is only one way to slide to any given item
 *        - to capture the state of this carousel from a parent component:
 *           ( for example, you may want to save the state of whether or not the carousel is currently sliding,
 *             in order to prevent a slide from being triggered while the carousel is already sliding )
 *           the onSlideBegin and onSlideEnd listeners are fired when an item slide/focus begins or ends, respectively
 *           both listeners take the item number of the slide that the carousel is/was sliding to
 *
 *
 *     other options:
 *
 *         - by default, (aka if `stretchChildren` is false) the strip is rendered as a flex container
 *            with children at default height aligned to the top
 *            this means that the carousel will take the height of the tallest child and all other children
 *            will have a gap underneath them
 *            pass `stretchChildren` as true to have children take up 100% height of the carousel
 *
 *         - by default, (aka if `fullHeight === false`), the carousel has the height of the tallest child
 *            pass `fullHeight` as true to have the carousel take up 100% height of the carousel's parent
 *            you will probably want to also set stretchChildren to true if you use this option
 *
 *********************************************************************/

const GenericCarousel = ({
  stretchChildren,
  draggable,
  fullHeight,
  initialSlide,
  nextSlide,
  nextDirection,
  onSlideBegin,
  onSlideEnd,
  autoplaying,
  autoplayInterval,
  ...props
}) => {
  const { t } = useTranslation();
  const children = Array.isArray(props.children)
    ? props.children
    : React.Children.toArray(props.children);

  const [currentSlide, setCurrentSlide] = useState(initialSlide || 0);
  // whether the carousel is currently in "focus mode"
  // that is, the user is interacting with it via keyboard and focus
  const [focusMode, setFocusMode] = useState(false);

  const sliderRef = useRef();
  const containerRef = useRef();

  const numSlides = children.length;
  // in the case of 1 slide, halfSlides should still be 1
  const halfSlides = Math.floor(children.length / 2) || 1;

  /***************************************
   * setup and checkup
   *
   *  all code that handles initializing logic and updating logic
   *
   *    setup:
   *      - initialize the timer that autoplays the carousel
   *        the first `autoplaying` value passed as a prop determines if the timer will start by default
   *
   *    checkup:
   *      - when `autoplaying` value changes, pause or play the timer, as necessary
   *      - when `nextSlide` value changes, slide to it!
   *
   **************************************/

  // set up the interval for automatically sliding
  const { isPlaying, pause, play, toggle, restart } = useInterval(
    () => {
      slideTo(currentSlide + 1, 1);
    },
    autoplayInterval || 5000,
    autoplaying
  );

  // when a new autoplaying value is passed, pause/play the animation
  useEffect(
    () => {
      autoplaying ? play() : pause();
    },
    [autoplaying]
  );

  // when a new nextSlide value is passed, slide to it (and restart the animation if needed)
  useEffect(
    () => {
      if (nextSlide !== currentSlide) {
        !focusMode && slideTo(nextSlide, nextDirection);
        focusMode && focusOn(nextSlide);
        isPlaying && restart();
      }
    },
    [nextSlide]
  );

  /*************************************************
   *  all code that handles logic for "sliding" of carousel items in slide mode
   *
   *  functions here:
   *  - determineTargetPosition:
   *      given an item number, what is its position in the currently rendered strip of items?
   *      (the rendered strip of items always has currentSlide in the center)
   *      a preferred direction can be given for cases with 1 or 2 slides
   *
   *  - slideTo:
   *       given an item number, slide to that item
   *      a preferred direction can be given for cases with 1 or 2 slides
   *      side effects:
   *       + the carousel slides to show that item
   *       + then the strip of items is re-rendered with that item in the center
   *
   *  - completeSlide:
   *      the strip may have had a partial sliding.
   *      given the percentage of that partial sliding, slide to the nearest item
   *      side effects:
   *        + same as slideTo
   *************************************************/

  // takes the `target` item index and returns its position in the currently rendered strip of items
  // `preferredDirection`: if 1, prefer to go to the right. if -1, prefer to go to the left
  // `preferredDirection` only affects the outcome in the case where there are 1 or 2 slides
  //   (where it would be valid to go either to the left or right)
  // in all other cases, there is only one possible direction to go in, so `preferredDirection` is ignored
  const determineTargetPosition = (targetSlide, preferredDirection) => {
    if (numSlides === 1 && preferredDirection) {
      return halfSlides + preferredDirection;
    }
    if (numSlides === 2 && targetSlide !== currentSlide) {
      return halfSlides + (preferredDirection || 1);
    }
    if (targetSlide - currentSlide < -halfSlides) {
      return targetSlide - currentSlide + numSlides + halfSlides;
    } else if (targetSlide - currentSlide > halfSlides) {
      return targetSlide - currentSlide - numSlides + halfSlides;
    } else {
      return targetSlide - currentSlide + halfSlides;
    }
  };

  // slide to a `target` item
  // note that `target` does not have to be in the range (0, numSlides - 1)
  // instead, we take mod(target, numSlides) to find the true target index
  // see notes about `preferredDirection` above
  const slideTo = (target, preferredDirection) => {
    const targetSlide = mod(target, numSlides);
    const targetPosition = determineTargetPosition(
      targetSlide,
      preferredDirection
    );
    // check if an animation is required or if the slide is already in the right place
    if (
      parsePercentage(sliderRef.current.style.left) !==
      targetPosition * -100
    ) {
      onSlideBegin && onSlideBegin(targetSlide);
      // animate the strip to move the target item into view
      sliderRef.current.style.transitionDuration = '0.2s';
      sliderRef.current.style.left = targetPosition * -100 + '%';
      // when the animation ends, reset and rerender the strip so that the target item is in the center
      const cleanUp = () => {
        setCurrentSlide(targetSlide);
        sliderRef.current.style.transitionDuration = '';
        sliderRef.current.style.left = -100 * halfSlides + '%';
        onSlideEnd && onSlideEnd(targetSlide);
        sliderRef.current.removeEventListener('transitionend', cleanUp);
      };
      sliderRef.current.addEventListener('transitionend', cleanUp);
    }
  };

  // a slide has been initiated by `percentageDelta` amount, but it's probably not a complete slide
  // either slide it back because the slide has not proceeded far enough (at least 20%)
  // or complete the intended slide interaction
  const completeSlide = percentageDelta => {
    if (percentageDelta < -20) {
      const slideDelta = Math.floor(-percentageDelta / 100) + 1;
      slideTo(currentSlide + slideDelta, 1);
    } else if (percentageDelta > 20) {
      const slideDelta = Math.floor(percentageDelta / 100) + 1;
      slideTo(currentSlide - slideDelta, -1);
    } else {
      slideTo(currentSlide);
    }
  };

  /************************************
   *  all code that handles "focusing" of carousel items in focus mode
   *  functions:
   *  - focusOn:
   *      given a target item number, shfit the strip so that item is in the viewable area
   *      there are no animations in focus mode
   *
   *  focus handler:
   *      on focusin of the carousel, change the mode of the carousel to "focus mode",
   *        pause the timer if it's currently autoplaying, and attach focusout handler
   *        this will cause the carousel strip to re-render in chronological order
   *        it also removes the drag handlers
   *      on focusout of the carousel, change the mode of the carousel back to "slide mode",
   *        play the timer if it was autoplaying before the focus happened, and clean up focusout handler
   *        this will cause the carousel strip to re-render in "infinite" order and put the drag handlers back
   *
   ************************************/

  const focusOn = target => {
    onSlideBegin && onSlideBegin(target);
    setCurrentSlide(target);
    // sometimes the transition is still set, so make sure it is removed
    sliderRef.current.style.transitionDuration = '';
    sliderRef.current.style.left = -100 * target + '%';
    onSlideEnd && onSlideEnd(target);
  };

  // when we focus on the strip, the carousel is no longer an infinite carousel
  // just make it a static list of items so the user can tab through to the end
  const handleFocus = e => {
    const cleanUp = e => {
      // first check if the element gaining focus is a child of the slider (i.e. it is one of the slides)
      // if it is, we don't want to switch out of focusmode yet
      if (!e.relatedTarget || !containerRef.current.contains(e.relatedTarget)) {
        isPlaying && play();
        setFocusMode(false);
        containerRef.current.removeEventListener('focusout', cleanUp);
      }
    };
    isPlaying && pause();
    // most browsers trigger a scroll when the user tabs to something off the screen
    // however, we're handling the movement of the carousel ourselves,
    // so make sure the scroll position is at 0 on every focus event
    containerRef.current.scrollLeft = 0;
    // we only need to initialize "focus mode" once
    if (!focusMode) {
      containerRef.current.addEventListener('focusout', cleanUp);
      setFocusMode(true);
      focusOn(0);
    }
  };

  /***********************************
   *  all code that handles "dragging" of carousel items
   *
   *     mouse handler:
   *         on mouse down, begin the drag + slide interaction and attach mousemove/mouseup handlers
   *         on mouse move, track the mouse movements and slide the carousel along with the mouse
   *         on mouse up, complete the slide interaction with completeSlide (above)
   *          and clean up mousemove/mouseup handlers
   *
   *
   *      touch handler:
   *         on touch start, begin the drag interaction and attach touchmove/toucheend handlers
   *           NOTE: we don't actually begin the slide interaction yet, because the user may actually be scrolling
   *         on touch move, check if user has moved 5px from the beginning of the motion
   *           if the user has moved 5px, and the movement is mostly vertical,
   *            kill the drag interaction and clean up touchmove/touchend handlers, allowing user to keep scrolling
   *           if the user has moved 5px, and the movement is mostly horizontal, begin the slide interaction
   *            prevent the default event (scroll), instead track the touch movements and slide the carousel along with the touch
   *         on touch end, complete the slide interaction with completeSlide (above)
   *          and clean up touchmove/touchend handlers
   *
   ***********************************/

  const dragStartMouse = e => {
    e.preventDefault();

    const width = containerRef.current.offsetWidth;
    const xInitial = e.clientX;
    const percentageInitial = parsePercentage(sliderRef.current.style.left);

    const dragAction = e => {
      e.preventDefault();
      console.log('fired');
      const xFinal = e.clientX;
      const percentageChange = ((xFinal - xInitial) / width) * 100;
      sliderRef.current.style.left = percentageInitial + percentageChange + '%';
    };

    const dragEnd = e => {
      const xFinal = e.clientX;
      const percentageChange = ((xFinal - xInitial) / width) * 100;
      completeSlide(percentageChange);
      deregisterEvents();
    };

    const registerEvents = () => {
      isPlaying && pause();
      document.addEventListener('mousemove', dragAction);
      document.addEventListener('mouseup', dragEnd);
    };

    const deregisterEvents = () => {
      document.removeEventListener('mousemove', dragAction);
      document.removeEventListener('mouseup', dragEnd);
      isPlaying && play();
    };

    registerEvents();
  };

  // we want to pause the automatic sliding when hovering over a slide
  const handleMouseOver = e => {
    if (isPlaying) {
      pause();
      const cleanUp = () => {
        play();
        sliderRef.current.removeEventListener('mouseleave', cleanUp);
      };
      sliderRef.current.addEventListener('mouseleave', cleanUp);
    }
  };

  return (
    <Fragment>
      <section
        styleName={classNames('carousel', {
          'full-height': fullHeight
        })}
        ref={containerRef}
        tabIndex={0}
        onFocus={handleFocus}
        role="marquee"
        aria-roledescription="carousel"
      >
        <ul
          styleName={classNames('carousel-strip', {
            'slide-mode': !focusMode,
            'focus-mode': focusMode,
            'stretch-children': stretchChildren,
            draggable: draggable
          })}
          ref={sliderRef}
          style={(!focusMode && { left: -100 * halfSlides + '%' }) || null}
          // we don't want to initiate focus on mouse click if dragging is disabled
          onMouseDown={
            (draggable && !focusMode && dragStartMouse) ||
            (e => {
              e.preventDefault();
            })
          }
          onMouseOver={(draggable && !focusMode && handleMouseOver) || null}
          role="listbox"
          aria-label="carousel-strip"
          aria-roledescription="carousel-strip"
        >
          {focusMode
            ? children.map((child, index) => {
                return (
                  <li
                    key={index}
                    tabIndex={0}
                    onFocus={e => {
                      e.preventDefault();
                      e.target.focus({ preventScroll: true });
                      focusOn(index);
                    }}
                    styleName={classNames({
                      'carousel-item': true,
                      active: index === currentSlide
                    })}
                    role="option"
                    aria-roledescription="slide"
                    aria-label={t('Slide {{index}} of {{numSlides}}', {
                      index: index + 1,
                      numSlides
                    })}
                  >
                    {child}
                  </li>
                );
              })
            : rangeWithCenteredIndex(currentSlide, numSlides).map(
                (slideIndex, index) => (
                  <li
                    key={
                      numSlides > 2
                        ? slideIndex
                        : index < numSlides
                        ? slideIndex
                        : index
                    }
                    styleName={classNames({
                      'carousel-item': true,
                      active: slideIndex === currentSlide
                    })}
                    role="option"
                    aria-roledescription="slide"
                    aria-label={t('Slide {{index}} of {{numSlides}}', {
                      index: slideIndex + 1,
                      numSlides
                    })}
                  >
                    {children[slideIndex]}
                  </li>
                )
              )}
        </ul>
      </section>
    </Fragment>
  );
};

export default DisplayComponent(GenericCarousel);
